"use client";

import { useEffect, useRef } from "react";

/**
 * Keyboard shortcuts shared by run-driven views (S4, S5, phase 2).
 *
 *   - `Enter`  → primary action: start a new run, or retry when a feedback
 *     is currently displayed. No-op while a run is running/finishing so we
 *     don't double-trigger.
 *   - `Escape` → cancel an in-flight run. No-op otherwise.
 *
 * Events originating from inputs / textareas / contentEditable surfaces
 * are ignored so the shortcut never hijacks typing.
 */

type RunStatus = "idle" | "running";

type RunKeyboardShortcutsArgs = {
  readonly status: RunStatus;
  /** `true` once a pass has finished: `Enter` then plays it again. */
  readonly hasFeedback: boolean;
  readonly onStart: () => void;
  readonly onRetry: () => void;
  readonly onCancel: () => void;
};

export function useRunKeyboardShortcuts(args: RunKeyboardShortcutsArgs): void {
  // Latché : les vues recréent leurs callbacks à chaque rendu, et l'écouteur
  // se détachait puis se rattachait sur `document` à chaque frappe de
  // touche. Il est posé une fois et lit l'état courant au moment de l'appui.
  const argsRef = useRef(args);

  useEffect(() => {
    argsRef.current = args;
  }, [args]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) {
        return;
      }

      const { status, hasFeedback, onStart, onRetry, onCancel } =
        argsRef.current;

      if (e.key === "Enter") {
        if (status !== "idle") {
          return;
        }

        e.preventDefault();

        if (hasFeedback) {
          onRetry();

          return;
        }

        onStart();

        return;
      }

      if (e.key === "Escape") {
        if (status !== "running") {
          return;
        }

        e.preventDefault();
        onCancel();
      }
    };

    document.addEventListener("keydown", handler);

    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, []);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;

  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
    return true;
  }

  return target.isContentEditable;
}
